import { observer } from "mobx-react-lite";
import React, { useRef, useState } from "react";
import { Select, Tooltip } from "antd";
import PremiumVersionIcon from "@/components/PremiumVersionIcon";
import { handleBackTop } from "@/components/BackTop";
import classNames from "classnames";
import "./index.less";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import { collectEvent } from "@/services/dataTrack";
import EwaybillAuthModal from "../EwaybillAuthModal";
import { useModalStore } from "@/hooks/Modal/hooks";
import ShopType from "@/enum/ShopType";
import ShopDTO from "@/dto/shop/ShopDTO";

interface PageNoSelectCoreProp {
    pageSize: number;
    onPageSizeChange: (value: number) => Promise<void>;
    shop?: ShopDTO;
}

const PRESET_PAGE_SIZES = [10, 25, 50, 100, 200, 500, 1000, 3000];
const MAX_PAGE_SIZE = 3000;

const PageNoSelectCore: React.FC<PageNoSelectCoreProp> = function (props) {
    const { pageSize, onPageSizeChange, shop } = props;
    const [searchValue, setSearchValue] = useState("");
    const lockRef = useRef(false);
    const modalStore = useModalStore();

    const updatePageSize = async (value: number) => {
        // 避免onInputKeyDown和onChange同时触发
        if (lockRef.current) return;
        lockRef.current = true;
        setTimeout(() => {
            lockRef.current = false;
        });

        if (!value || value <= 0) return;

        let finalValue = value;
        if (finalValue > MAX_PAGE_SIZE) finalValue = MAX_PAGE_SIZE;
        if (finalValue === pageSize) return;

        await onPageSizeChange(finalValue);
        handleBackTop();
        setSearchValue("");

        if (!PRESET_PAGE_SIZES.includes(finalValue)) {
            collectEvent(DataTrackEvent.CUSTOM_INPUT_PAGE_SIZE, {
                page_size: value,
            });
        }
    };

    const isError = searchValue && Number(searchValue) > MAX_PAGE_SIZE;

    return (
        <>
            <Tooltip title="每页最多显示3000条" open={isError}>
                <Select
                    showSearch={{
                        filterOption: false,
                        searchValue: searchValue,
                        onSearch: (val) => {
                            const numericVal = val.replace(/\D/g, "");

                            /**  这里要延迟执行，让onBlur中拿到最新的值 */
                            setTimeout(() => {
                                setSearchValue(numericVal);
                            });
                        },
                    }}
                    size="small"
                    style={{ minWidth: 75 }}
                    value={pageSize}
                    className={classNames("page-size-select", { "border-red": isError })}
                    onChange={(val) => {
                        updatePageSize(Number(val));
                    }}
                    onInputKeyDown={(e) => {
                        if (e.key === "Enter" && searchValue) {
                            const inputNum = Number(searchValue);

                            updatePageSize(inputNum);
                        }
                    }}
                    onBlur={() => {
                        const inputNum = Number(searchValue);

                        if (Number.isNaN(inputNum)) return;

                        updatePageSize(inputNum);
                    }}
                    popupMatchSelectWidth={false}
                    getPopupContainer={() => document.querySelector(".bottom-operation-bar") || document.body}
                    options={[
                        ...(searchValue && !PRESET_PAGE_SIZES.includes(Number(searchValue))
                            ? [
                                  {
                                      key: "custom-input",
                                      value: Number(searchValue),
                                      label: <>使用输入的:{searchValue}</>,
                                  },
                              ]
                            : []),
                        ...(PRESET_PAGE_SIZES.map((size) => ({
                            key: size,
                            value: size,
                            label: (
                                <>
                                    <span>{size} </span>
                                    {size >= 1000 && <PremiumVersionIcon className="ml5" />}
                                </>
                            ),
                        })) ?? []),
                        ...(!searchValue && !PRESET_PAGE_SIZES.includes(pageSize)
                            ? [
                                  {
                                      key: pageSize,
                                      value: pageSize,
                                      label: <span>{pageSize} (自定义)</span>,
                                  },
                              ]
                            : []),
                    ]}
                />
            </Tooltip>

            <span className="ml5 text-gray small">可直接输入数量</span>
            {ShopType.ALIBABA === shop?.type ? (
                <span
                    className="fs12 ml10 error fb cursor-pointer"
                    style={{ padding: 0 }}
                    onClick={() => {
                        modalStore.openModal(EwaybillAuthModal, {
                            shopId: shop?.id,
                        });
                    }}
                >
                    开通密文面单打单
                </span>
            ) : null}
        </>
    );
};

export default observer(PageNoSelectCore);
